import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/ui/dialog";
import { ScrollArea } from "@shared/ui/scroll-area";
import {
  BookOpen,
  Box,
  Database,
  Grid,
  HelpCircle,
  Image as ImageIcon,
  Layers,
  Minus,
  MousePointer,
  Move,
  Settings,
  Square,
  Type,
} from "lucide-react";
import React from "react";

/* ----------------------------- */
/* Extracted static lists ONLY   */
/* ----------------------------- */

const MANAGING_ELEMENTS = [
  {
    icon: BookOpen,
    title: "Add",
    desc: "Click any component in the left sidebar to add it to the center of the canvas.",
  },
  {
    icon: MousePointer,
    title: "Select",
    desc: "Click an element to select it. Hold Shift or Ctrl to select multiple elements.",
  },
  {
    icon: Move,
    title: "Move & Resize",
    desc: "Drag elements to move. Drag corner handles to resize.",
  },
  {
    icon: Layers,
    title: "Context Menu",
    desc: "Right-click any element for quick actions like Layering, Grouping, or Deleting.",
  },
];

const BASIC_ELEMENTS = [
  { icon: Type, name: "Text Block" },
  { icon: ImageIcon, name: "Logo/Image" },
  { icon: Minus, name: "Divider Line" },
  { icon: Square, name: "Rectangle Box" },
  { icon: Minus, name: "Perforation Line" },
];

const COMPONENTS_AND_FIELDS = [
  {
    title: "Customer Information",
    sections: [
      {
        label: "Consumer Account",
        items: [
          "Customer Name",
          "Email",
          "Phone",
          "Mobile",
          "Billing Address",
          "Service Address",
          "Customer ID",
          "Legacy ID",
          "GST Number",
          "PAN Number",
        ],
      },
      {
        label: "Plan Details",
        items: ["Tariff Category"],
      },
    ],
  },
  {
    title: "Account Details",
    sections: [
      {
        label: "Consumer Account",
        items: [
          "Account Number",
          "Legacy Account Number",
          "Utility Services",
          "Mailing Address",
          "Service Address",
          "Security Deposit",
          "Connection Status",
          "Connection Date",
        ],
      },
      {
        label: "Plan Details",
        items: ["Plan", "Category", "Sub Category", "Sanctioned Load", "Phase"],
      },
      {
        label: "Meter Details",
        items: ["Meter Number"],
      },
    ],
  },
  {
    title: "Invoice Details",
    sections: [
      {
        label: "Invoice Details",
        items: [
          "Invoice No",
          "Invoice Date",
          "Invoice Period",
          "Due Date",
          "Amount",
        ],
      },
      {
        label: "Total and Adjustments",
        items: [
          "Water Usage Charges",
          "Water Service Charges",
          "Admin Fees",
          "Tax",
          "Available Credit",
          "Total Amount",
        ],
      },
    ],
  },
  {
    title: "Payment Stub Section",
    sections: [
      {
        label: "Payment Stub",
        items: [
          "Invoice Date",
          "Invoice No",
          "Account Name",
          "Account No",
          "Due Date",
          "Total Amount",
        ],
      },
    ],
  },
  {
    title: "Consumption and Service Charges",
    description: "Configurable table with toggleable columns and sub-totals.",
  },
  {
    title: "Meter Reading Components",
    sections: [
      {
        label: "Components",
        items: [
          "Current Meter Reading",
          "Old Meter Details",
          "Replaced Meter Details",
          "Meter Replace – Combined",
        ],
      },
    ],
  },
  {
    title: "Usage History Chart",
    description:
      "Bar graph for last 12 bills with configurable axes, labels, rotation, and billing periods.",
  },
  {
    title: "Payment History",
    description:
      "Table for up to last 12 bills with configurable columns (Bill Date, Amount, Payment).",
  },
  {
    title: "Perforation Line",
    description:
      "Customizable line with adjustable height and vertical positioning.",
  },
  {
    title: "Basic Elements",
    sections: [
      {
        label: "Elements",
        items: [
          "Text Block (Rich Text)",
          "Logo / Image",
          "Divider Line",
          "Rectangle Box",
        ],
      },
    ],
  },
];

export const DesignerGuide: React.FC = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 border-gray-300 text-gray-700 font-bold px-4 hover:bg-gray-100 active:scale-95 transition-all"
        >
          <HelpCircle size={14} className="mr-2 text-blue-600" />
          Guide
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
        <DialogHeader className="p-6 border-b bg-gray-50/50">
          <DialogTitle className="flex items-center gap-2 text-2xl font-bold text-gray-800">
            <BookOpen className="text-blue-600" />
            Template Editor Guide
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-full max-h-[calc(90vh-120px)] p-8">
          <div className="space-y-10 pb-10">
            {/* Getting Started */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-black">
                  01
                </span>
                Getting Started
              </h3>
              <p className="text-gray-600 leading-relaxed bg-blue-50/30 p-4 rounded-xl border border-blue-50">
                Welcome to the Template Editor. This visual tool allows you to
                design professional bill templates using a drag-and-drop
                interface. You can create single or double-sided templates,
                customize layouts, and configure dynamic data fields.
              </p>
            </section>
            {/* Canvas Tools */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center text-sm font-black">
                  02
                </span>
                Canvas Tools
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="font-bold text-gray-800 mb-2">
                    Front/Back Sides
                  </div>
                  <p className="text-sm text-gray-500">
                    Use the toggle in the top-left to switch between editing the
                    front and back pages (if enabled).
                  </p>
                </div>
                <div className="p-4 border rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="font-bold text-gray-800 mb-2">
                    Grid & Snapping
                  </div>
                  <p className="text-sm text-gray-500">
                    Toggle the grid view and snap-to-grid features in the
                    toolbar for precise alignment.
                  </p>
                </div>
                <div className="p-4 border rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="font-bold text-gray-800 mb-2">Zoom</div>
                  <p className="text-sm text-gray-500">
                    The canvas automatically scales, but you can check the zoom
                    level in the top-right corner.
                  </p>
                </div>
              </div>
            </section>
            {/* Managing Elements */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-black">
                  03
                </span>
                Managing Elements
              </h3>
              <div className="space-y-3">
                {MANAGING_ELEMENTS.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 p-4 bg-gray-50/50 rounded-xl"
                  >
                    <div className="mt-1">
                      <item.icon size={18} className="text-gray-400" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-800">
                        {item.title}
                      </div>
                      <p className="text-sm text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
            {/* Advanced Organization */}
            <section>
              <span className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-black">
                04
              </span>
              Advanced Organization
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="text-blue-400 font-bold mb-1">Grouping</div>
                  <p className="text-sm text-gray-400">
                    Select multiple items and click "Group" in the sidebar or
                    right-click menu. Grouped items move together.
                  </p>
                </div>
                <div>
                  <div className="text-blue-400 font-bold mb-1">
                    Layering (Z-Index)
                  </div>
                  <p className="text-sm text-gray-400">
                    Use the "Bring to Front" or "Send to Back" controls to
                    manage overlapping elements.
                  </p>
                </div>
              </div>
            </section>
            {/* Properties & Configuration */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-black">
                  04
                </span>
                Properties & Configuration
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-5 border-2 border-blue-100 rounded-2xl bg-blue-50/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Settings className="text-blue-600" />
                    <div className="font-black uppercase tracking-widest text-xs text-blue-600">
                      Properties Tab
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Controls visual aspects: Position (X, Y), Size (Width,
                    Height), Rotation, Background Colors, Borders, and Layering
                    order.
                  </p>
                </div>

                <div className="p-5 border-2 border-purple-100 rounded-2xl bg-purple-50/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Database className="text-purple-600" />
                    <div className="font-black uppercase tracking-widest text-xs text-purple-600">
                      Configuration Tab
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Controls content and data for dynamic components. See the
                    Components & Fields section below for details.
                  </p>
                </div>
              </div>
            </section>
            {/* Components & Fields */}
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center gap-2">
                <Grid className="w-5 h-5 text-gray-700" />
                <h2 className="text-lg font-semibold">
                  Components & Available Fields
                </h2>
              </div>

              <p className="text-sm text-gray-600">
                Each component has configurable fields that can be shown/hidden
                in the Configuration tab:
              </p>

              <div className="space-y-4">
                {COMPONENTS_AND_FIELDS.map((component, index) => (
                  <div key={index} className="rounded-lg border bg-white p-4">
                    {/* Card Title */}
                    <div className="flex items-center gap-2 mb-3">
                      <Box className="w-4 h-4 text-gray-700" />
                      <h3 className="text-sm font-medium">{component.title}</h3>
                    </div>

                    {/* Sections */}
                    {component.sections && (
                      <div className="space-y-2">
                        {component.sections.map((section, i) => (
                          <div key={i} className="text-sm text-gray-600">
                            <span className="font-medium text-gray-700">
                              {section.label}:
                            </span>{" "}
                            {section.items.join(", ")}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Description-only cards */}
                    {component.description && (
                      <p className="text-sm text-gray-600">
                        {component.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
